import React from 'react';
import styled from 'styled-components';
import { Trans } from '@lingui/macro';
import type { Card as CardModel } from '../types';
import { CardTile } from './CardTile';
import { CardCarousel } from './CardCarousel';
import { Balance } from './Balance';
import { StatusChip } from './StatusChip';
import { MOBILE, tok } from './styles';

/**
 * Responsive card region (PRD §1 / §3). Two viewport-driven layouts that share
 * the same `Card` visual — purely CSS `@media` at 697px, never a JS/platform
 * branch:
 *
 * - **Desktop (>697px):** "Welcome {firstName}" above a wrapping grid of
 *   `CardTile`s (each with its own balance + status).
 * - **Mobile (≤697px):** a white rounded `top-part` wrapping the welcome, a
 *   single-card `CardCarousel`, and the selected card's balance + status.
 */

const Welcome = styled.h1`
  margin: 0;
  font-weight: 700;
  font-size: 24px;
  line-height: 32px;
  color: ${({ theme }) => tok(theme, 'general_font_primary', '#000000')};

  @media (max-width: ${MOBILE}) {
    font-size: 20px;
    line-height: 30px;
  }
`;

const Desktop = styled.div`
  display: flex;
  flex-direction: column;
  /* 26px from the "Welcome" title to the card row, matching the legacy. */
  gap: 26px;

  @media (max-width: ${MOBILE}) {
    display: none;
  }
`;

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
`;

const MobileTop = styled.div`
  display: none;

  @media (max-width: ${MOBILE}) {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 20px 0 16px;
    border-radius: 16px;
    overflow: hidden;
    background: ${({ theme }) => tok(theme, 'containers_shadowed_background', '#ffffff')};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
`;

const Gutter = styled.div`
  padding: 0 32px;
`;

const BalanceSection = styled(Gutter)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

type Props = {
  firstName: string;
  cards: CardModel[];
  selectedIdx: number;
  onSelect: (idx: number) => void;
  onNavigate: (route: string) => void;
};

export const CardSection: React.FC<Props> = ({
  firstName,
  cards,
  selectedIdx,
  onSelect,
  onNavigate,
}) => {
  const selected = cards[Math.min(selectedIdx, cards.length - 1)];
  return (
    <>
      <Desktop>
        <Welcome>
          <Trans>Welcome {firstName}</Trans>
        </Welcome>
        <Grid>
          {cards.map((card) => (
            <CardTile key={card.id} card={card} onNavigate={onNavigate} />
          ))}
        </Grid>
      </Desktop>

      <MobileTop>
        <Gutter>
          <Welcome>
            <Trans>Welcome {firstName}</Trans>
          </Welcome>
        </Gutter>
        <CardCarousel
          cards={cards}
          selectedIdx={selectedIdx}
          onSelect={onSelect}
          onNavigate={onNavigate}
        />
        {selected && (
          <BalanceSection>
            <Balance card={selected} />
            <StatusChip status={selected.status} />
          </BalanceSection>
        )}
      </MobileTop>
    </>
  );
};
